import { GitBranch, PanelRight } from 'lucide-react';
import { IncidenteSelo } from '@/components/patterns/incidente-badges';
import { NoPainelBadge, StatusBuildBadge } from '@/components/patterns/status-badge';
import { Button } from '@/components/ui/button';
import type { BuildResumo } from '@/data/types';
import { DownloadLinks } from '@/features/versao/download-links';
import type { AcaoStatus } from '@/lib/capabilities';
import { formatarDataHora, formatarRelativo, plural } from '@/lib/format';
import { cn } from '@/lib/utils';
import { MenuAcoes } from './PublicacaoCard';

/**
 * Card compacto da vista "Feed": uma publicação em poucas linhas — versão em
 * destaque (mono), status, selos, branch/ramo, horário relativo (absoluto no
 * `title`), nº de artefatos, downloads e o botão de abrir o detalhe.
 */
export function CardCompacto({
  build,
  ativo,
  acoesRapidas,
  aoAbrir,
  aoExecutarAcao,
  emAcao,
}: {
  build: BuildResumo;
  ativo?: boolean;
  acoesRapidas: AcaoStatus[];
  aoAbrir: (build: BuildResumo) => void;
  aoExecutarAcao: (build: BuildResumo, acao: AcaoStatus) => void;
  emAcao?: boolean;
}) {
  return (
    <li>
      <article
        aria-label={`Versão ${build.versao}`}
        className={cn(
          'flex flex-col gap-2.5 rounded-card border bg-bg-surface px-4 py-3 shadow-sm transition-colors duration-fast ease-standard',
          ativo ? 'border-primary-border bg-primary-wash' : 'border-border hover:border-border-strong',
        )}
      >
        <div className="flex items-start gap-2">
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1.5">
            <button
              type="button"
              onClick={() => aoAbrir(build)}
              aria-label={`Abrir o detalhe da versão ${build.versao}`}
              className="rounded-button text-left font-mono text-card-title tabular-nums text-on-surface underline-offset-4 transition-colors duration-fast ease-standard hover:text-primary hover:underline"
            >
              {build.versao}
            </button>
            <StatusBuildBadge status={build.status} />
            {build.no_painel ? <NoPainelBadge /> : null}
            <IncidenteSelo
              quantidade={build.incidentes_abertos}
              severidade={build.incidente_severidade_max}
              versao={build.versao}
            />
          </div>
          <MenuAcoes build={build} acoes={acoesRapidas} aoAbrir={aoAbrir} aoExecutar={aoExecutarAcao} ocupado={emAcao} />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <p className="flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1 text-cell text-on-surface-muted">
            <span className="inline-flex items-center gap-1 rounded-chip border border-border bg-bg-subtle px-1.5 py-px font-mono text-label font-normal text-on-surface">
              <GitBranch className="size-3 text-icon" aria-hidden="true" />
              {build.branch}
            </span>
            <span>
              Ramo <span className="font-mono text-on-surface">{build.ramo}</span>
            </span>
            <span aria-hidden="true">·</span>
            <time dateTime={build.publicado_em} title={formatarDataHora(build.publicado_em)}>
              {formatarRelativo(build.publicado_em)}
            </time>
            <span aria-hidden="true">·</span>
            <span>{plural(build.total_artefatos, 'artefato', 'artefatos')}</span>
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <DownloadLinks links={build.links} versao={build.versao} />
            <Button variant="ghost" size="sm" onClick={() => aoAbrir(build)}>
              <PanelRight aria-hidden="true" />
              Detalhes
              <span className="sr-only"> da versão {build.versao}</span>
            </Button>
          </div>
        </div>
      </article>
    </li>
  );
}

